import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Printer, Mail, Trash2, Save, Image } from 'lucide-react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDevisDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const printRef = useRef();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('nouveau');

  const getAuthHeader = () => {
    const password = localStorage.getItem('idmat_admin');
    return {
      headers: {
        'Authorization': 'Basic ' + btoa('admin:' + password)
      }
    };
  };

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const response = await axios.get(`${API}/admin/quotes/${id}`, getAuthHeader());
        setQuote(response.data);
        setNotes(response.data.notes || '');
        setStatus(response.data.status);
      } catch (error) {
        toast.error('Devis non trouvé');
        navigate('/admin/devis');
      } finally {
        setLoading(false);
      }
    };
    fetchQuote();
  }, [id, navigate]);

  const handleSave = async () => {
    try {
      await axios.put(`${API}/admin/quotes/${id}`, { status, notes }, getAuthHeader());
      toast.success('Devis mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce devis ?')) return;
    
    try {
      await axios.delete(`${API}/admin/quotes/${id}`, getAuthHeader());
      toast.success('Devis supprimé');
      navigate('/admin/devis');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Devis - ${quote.first_name} ${quote.last_name}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; }
          .header { border-bottom: 3px solid #F5C500; padding-bottom: 20px; margin-bottom: 30px; }
          .logo { font-size: 24px; font-weight: bold; }
          .logo span { color: #F5C500; }
          h1 { margin: 0; font-size: 20px; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
          .info-block h3 { color: #666; font-size: 12px; text-transform: uppercase; margin-bottom: 5px; }
          .info-block p { margin: 0; font-size: 14px; }
          .description { background: #f5f5f5; padding: 20px; margin-bottom: 30px; }
          .footer { margin-top: 50px; text-align: center; color: #666; font-size: 12px; }
          @media print { body { print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">IDMAT<span>SERVICE</span></div>
          <p>27 avenue des artisans, 97436 Saint-Leu, La Réunion</p>
          <p>Tél: 06 93 52 64 49 | Email: contact@idmatoi.com</p>
        </div>
        
        <h1>DEMANDE DE DEVIS #${quote.id.substring(0, 8).toUpperCase()}</h1>
        <p style="color: #666;">Date: ${new Date(quote.created_at).toLocaleDateString('fr-FR')}</p>
        
        <div class="info-grid">
          <div class="info-block">
            <h3>Client</h3>
            <p><strong>${quote.first_name} ${quote.last_name}</strong></p>
            <p>${quote.email}</p>
            <p>${quote.phone}</p>
          </div>
          <div class="info-block">
            <h3>Engin</h3>
            <p><strong>${quote.equipment_brand}</strong></p>
            <p>Modèle: ${quote.equipment_model}</p>
            <p>Service: ${quote.service_type === 'reparation' ? 'Réparation' : quote.service_type === 'peinture' ? 'Peinture' : 'Entretien'}</p>
          </div>
        </div>
        
        <div class="description">
          <h3 style="margin-top: 0;">Description du problème</h3>
          <p>${quote.description}</p>
        </div>
        
        ${quote.notes ? `<div class="description"><h3 style="margin-top: 0;">Notes internes</h3><p>${quote.notes}</p></div>` : ''}
        
        <div class="footer">
          <p>IDMAT Service - Réparation & Peinture d'Engins de Chantier</p>
          <p>Ce document est une demande de devis et non un engagement contractuel.</p>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const getServiceLabel = (type) => {
    const labels = {
      reparation: 'Réparation',
      peinture: 'Peinture',
      entretien: 'Entretien'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-[#F5C500] border-t-transparent rounded-full"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!quote) return null;

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />
      <div data-testid="admin-devis-detail" ref={printRef}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Link 
              to="/admin/devis" 
              className="p-2 hover:bg-gray-100"
              data-testid="back-btn"
            >
              <ArrowLeft size={24} />
            </Link>
            <div>
              <h1 
                className="text-3xl font-bold text-[#1a1a1a]"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Devis #{quote.id.substring(0, 8).toUpperCase()}
              </h1>
              <p className="text-gray-500">
                Créé le {new Date(quote.created_at).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 transition-colors"
              data-testid="print-btn"
            >
              <Printer size={18} />
              Imprimer
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
              data-testid="delete-btn"
            >
              <Trash2 size={18} />
              Supprimer
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client Info */}
            <div className="bg-white p-6 shadow-sm">
              <h2 
                className="text-xl font-bold text-[#1a1a1a] mb-4 border-b border-gray-100 pb-4"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Informations Client
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Nom complet</p>
                  <p className="font-medium text-lg">{quote.first_name} {quote.last_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Téléphone</p>
                  <a href={`tel:${quote.phone}`} className="font-medium text-lg text-[#F5C500] hover:underline">
                    {quote.phone}
                  </a>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Email</p>
                  <a href={`mailto:${quote.email}`} className="font-medium text-[#F5C500] hover:underline">
                    {quote.email}
                  </a>
                </div>
              </div>
            </div>

            {/* Equipment Info */}
            <div className="bg-white p-6 shadow-sm">
              <h2 
                className="text-xl font-bold text-[#1a1a1a] mb-4 border-b border-gray-100 pb-4"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Informations Engin
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Marque</p>
                  <p className="font-medium text-lg">{quote.equipment_brand}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Modèle</p>
                  <p className="font-medium text-lg">{quote.equipment_model}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Service demandé</p>
                  <p className="font-medium text-lg">{getServiceLabel(quote.service_type)}</p>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="bg-white p-6 shadow-sm">
              <h2 
                className="text-xl font-bold text-[#1a1a1a] mb-4 border-b border-gray-100 pb-4"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Description du Problème
              </h2>
              <p className="text-gray-700 whitespace-pre-wrap">{quote.description}</p>
            </div>

            {/* Photo */}
            {quote.photo_data && (
              <div className="bg-white p-6 shadow-sm">
                <h2 
                  className="text-xl font-bold text-[#1a1a1a] mb-4 border-b border-gray-100 pb-4"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  Photo de l'Engin
                </h2>
                <img 
                  src={`data:image/jpeg;base64,${quote.photo_data}`} 
                  alt="Engin"
                  className="max-w-full h-auto max-h-96 object-contain"
                />
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status & Notes */}
            <div className="bg-white p-6 shadow-sm">
              <h2 
                className="text-xl font-bold text-[#1a1a1a] mb-4 border-b border-gray-100 pb-4"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Gestion
              </h2>
              
              <div className="mb-4">
                <label className="block text-sm text-gray-500 uppercase tracking-wide mb-2">
                  Statut
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 focus:border-[#F5C500] focus:outline-none"
                  data-testid="status-select"
                >
                  <option value="nouveau">Nouveau</option>
                  <option value="en_cours">En cours</option>
                  <option value="traite">Traité</option>
                  <option value="refuse">Refusé</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm text-gray-500 uppercase tracking-wide mb-2">
                  Notes internes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={5}
                  className="w-full px-4 py-2 border border-gray-200 focus:border-[#F5C500] focus:outline-none"
                  placeholder="Ajoutez des notes..."
                  data-testid="notes-textarea"
                />
              </div>

              <button
                onClick={handleSave}
                className="w-full flex items-center justify-center gap-2 bg-[#F5C500] text-[#1a1a1a] py-3 font-bold uppercase tracking-wide hover:bg-[#ddb200] transition-colors"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                data-testid="save-btn"
              >
                <Save size={18} />
                Enregistrer
              </button>
            </div>

            {/* Quick Actions */}
            <div className="bg-white p-6 shadow-sm">
              <h2 
                className="text-xl font-bold text-[#1a1a1a] mb-4 border-b border-gray-100 pb-4"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Actions Rapides
              </h2>
              <div className="space-y-2">
                <a
                  href={`mailto:${quote.email}?subject=Votre demande de devis IDMAT Service&body=Bonjour ${quote.first_name},`}
                  className="w-full flex items-center gap-2 px-4 py-3 border border-gray-200 hover:border-[#F5C500] transition-colors"
                  data-testid="email-client"
                >
                  <Mail size={18} className="text-[#F5C500]" />
                  Envoyer un email
                </a>
                <a
                  href={`tel:${quote.phone}`}
                  className="w-full flex items-center gap-2 px-4 py-3 border border-gray-200 hover:border-[#F5C500] transition-colors"
                >
                  📞 Appeler le client
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDevisDetail;
